/**
 * Security Settings Component
 *
 * Password change functionality with OWASP-compliant validation.
 * Requires current password for verification.
 *
 * TASK-168: Settings Page - Security Tab
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { httpClient } from '../../lib/httpClient';
import TwoFAEnrollFlow from '../auth/TwoFAEnrollFlow';

// Password validation schema (OWASP compliant)
const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordSchema>;

export default function SecuritySettings() {
  const { t } = useTranslation('settings');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = async (data: PasswordFormData) => {
    try {
      await httpClient.put('/api/settings/password', {
        current_password: data.currentPassword,
        new_password: data.newPassword,
        confirm_password: data.confirmPassword,
      });

      toast.success(t('security.password_changed', 'Password updated successfully'));
      reset(); // Clear form on success
    } catch (error: any) {
      console.error('Password change failed:', error);

      if (error.response?.status === 401) {
        toast.error(t('security.wrong_password', 'Current password is incorrect'));
      } else if (error.response?.status === 400) {
        toast.error(t('security.validation_error', 'Password does not meet requirements'));
      } else {
        toast.error(t('security.error', 'Failed to update password. Please try again.'));
      }
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle>{t('security.title', 'Security Settings')}</CardTitle>
        <CardDescription>
          {t('security.description', 'Update your password to keep your account secure')}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="text-sm font-medium text-gray-900 mb-1 block">
              {t('security.current_password', 'Current Password')}
            </label>
            <div className="relative">
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                {...register('currentPassword')}
                error={!!errors.currentPassword}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPasswords.current ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.currentPassword.message}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label className="text-sm font-medium text-gray-900 mb-1 block">
              {t('security.new_password', 'New Password')}
            </label>
            <div className="relative">
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                {...register('newPassword')}
                error={!!errors.newPassword}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPasswords.new ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.newPassword.message}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="text-sm font-medium text-gray-900 mb-1 block">
              {t('security.confirm_password', 'Confirm New Password')}
            </label>
            <div className="relative">
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                {...register('confirmPassword')}
                error={!!errors.confirmPassword}
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.confirmPassword.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting
              ? t('common:labels.loading', 'Loading...')
              : t('security.change_password', 'Change Password')
            }
          </Button>
        </form>

        {/* Password Requirements */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h4 className="font-medium text-sm text-gray-900 mb-2">
            {t('security.requirements', 'Password Requirements')}
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• {t('security.req_length', 'At least 8 characters long')}</li>
            <li>• {t('security.req_uppercase', 'Contains at least one uppercase letter (A-Z)')}</li>
            <li>• {t('security.req_lowercase', 'Contains at least one lowercase letter (a-z)')}</li>
            <li>• {t('security.req_number', 'Contains at least one number (0-9)')}</li>
            <li>• {t('security.req_special', 'Contains at least one special character (!@#$%^&*)')}</li>
          </ul>
        </div>
      </CardContent>
    </Card>

    {/* TASK-631: per-user TOTP enrollment */}
    <TwoFASection />
    </>
  );
}

function TwoFASection() {
  const { t } = useTranslation('settings');
  const [status, setStatus] = useState<{ enabled: boolean; confirmed_at: string | null } | null>(null);
  const [isEnrolling, setIsEnrolling] = useState(false);

  const loadStatus = async () => {
    try {
      const data = await httpClient.get<{ enabled: boolean; confirmed_at: string | null }>(
        '/api/auth/2fa/status'
      );
      setStatus(data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>{t('security.twofa_title', 'Two-Factor Authentication')}</CardTitle>
        <CardDescription>
          {t(
            'security.twofa_description',
            'Add a second factor with an authenticator app (TOTP)'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {status?.enabled ? (
          <p className="text-sm text-green-700">
            {t('security.twofa_enabled', 'Two-factor authentication is enabled')}
            {status.confirmed_at && (
              <span className="text-gray-500">
                {' '}
                ({new Date(status.confirmed_at).toLocaleDateString()})
              </span>
            )}
          </p>
        ) : isEnrolling ? (
          <TwoFAEnrollFlow
            onEnabled={() => {
              setIsEnrolling(false);
              loadStatus();
            }}
          />
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {t('security.twofa_disabled', 'Two-factor authentication is not enabled.')}
            </p>
            <Button type="button" onClick={() => setIsEnrolling(true)}>
              {t('security.twofa_enable', 'Enable 2FA')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
